import axios from 'axios';
import { computed, ref } from 'vue';
import { GOOGLE_SEARCH_CX_KEY, GOOGLE_SEARCH_API_KEY } from '@model/const/api-keys';
import { getLanguageCodeFromLocale } from '@utils/languages';

const localeMapping: Record<string, string> = {
    'en-us': 'lang_en',
    'en-en': 'lang_en',
    'en-eu': 'lang_en',
    'en-sg': 'lang_en',
    'bg-bg': 'lang_bg',
    'cs-cz': 'lang_cz',
    'de-de': 'lang_de',
    'es-es': 'lang_es',
    'es-mx': 'lang_es',
    'fr-fr': 'lang_fr',
    'he-il': 'lang_iw',
    'hu-hu': 'lang_hu',
    'it-it': 'lang_it',
    'ja-jp': 'lang_ja',
    'ko-kr': 'lang_ko',
    'nl-nl': 'lang_nl',
    'pl-pl': 'lang_pl',
    'pt-br': 'lang_pt',
    'ro-ro': 'lang_ro',
    'tr-tr': 'lang_tr',
    'zh-cn': 'lang_zh-CN',
    'zh-tw': 'lang_zh-TW',
    'sv-se': 'lang_sv',
};

const PAGE_SIZE = 10;
const MAX_PAGES = 10;
const MAX_RESULTS = 100;
const SEARCH_URL = 'https://customsearch.googleapis.com/customsearch/v1';

export function buildSiteFilterClause(links: string[], locale: string): string {
    const language = getLanguageCodeFromLocale(locale) ?? '';

    return (links || [])
        .filter(Boolean)
        .map((link) => link.replace(/^https?:\/\//, ''))
        .map((link) => link.replace('$LOCALE', locale))
        .map((link) => link.replace('$LANG', language))
        .map((link) => `site:https://${link}`)
        .join(' OR ');
}

export interface GoogleSearchParams {
    query: string;
    locale: string;
    filterClause?: string;
    page?: number;
}

export interface GoogleSearchResponse {
    items?: any[];
    searchInformation?: { totalResults?: string | number };
    queries?: { request?: { searchTerms?: string }[] };
    spelling?: { correctedQuery?: string };
}

export function formattedLinkPath(path: string): string {
    if (!path) return '';
    return path
        .replace(/^https?:\/\//gm, '')
        .replace(/[<][/]/g, '::protected::') // protect closing html tags "</" from split
        .split('/')
        .filter(Boolean)
        .join(' › ')
        .replace(/::protected::/g, '</'); // restore the closing tags from protected tokens
}

export function useGoogleSearch() {
    const results = ref([]);
    const totalResults = ref(0);
    const page = ref(1);
    const isLoading = ref(false);
    const lastResponse = ref<GoogleSearchResponse | null>(null);

    const totalPages = computed(() => {
        const capped = Math.min(totalResults.value, MAX_RESULTS);
        const total = Math.ceil(capped / PAGE_SIZE);
        return total < MAX_PAGES ? total : MAX_PAGES;
    });

    async function search({ query, locale, filterClause = '', page: pageNumber = 1 }: GoogleSearchParams) {
        page.value = pageNumber;
        isLoading.value = true;

        const start = (pageNumber - 1) * PAGE_SIZE + 1;
        const fullQuery = filterClause ? `${query} ${filterClause}` : query;

        try {
            const response = await axios.request({
                method: 'GET',
                url: SEARCH_URL,
                timeout: 10000,
                params: {
                    cx: GOOGLE_SEARCH_CX_KEY,
                    key: GOOGLE_SEARCH_API_KEY,
                    q: fullQuery,
                    start,
                    num: PAGE_SIZE,
                    lr: localeMapping[locale],
                },
            });
            lastResponse.value = response.data;
            results.value = response.data?.items || [];
            totalResults.value = Number(response.data?.searchInformation?.totalResults) || 0;
        } catch {
            lastResponse.value = null;
            results.value = [];
            totalResults.value = 0;
        } finally {
            isLoading.value = false;
        }

        return lastResponse.value;
    }

    function reset() {
        results.value = [];
        totalResults.value = 0;
        page.value = 1;
        lastResponse.value = null;
    }

    return {
        results,
        totalResults,
        page,
        totalPages,
        pageSize: PAGE_SIZE,
        isLoading,
        lastResponse,
        search,
        reset,
    };
}
